/**
 * NHTSA VIN Decoder API Integration
 * Free government API for decoding Vehicle Identification Numbers
 * https://vpic.nhtsa.dot.gov/api/
 */

const NHTSA_API_BASE = 'https://vpic.nhtsa.dot.gov/api/vehicles'

/**
 * Decode a VIN using the NHTSA vPIC API
 * @param {string} vin - 17-character Vehicle Identification Number
 * @returns {Promise<object>} - Decoded vehicle information
 */
export async function decodeVIN(vin) {
  if (!vin || vin.length !== 17) {
    throw new Error('VIN must be exactly 17 characters')
  }

  const url = `${NHTSA_API_BASE}/DecodeVin/${vin}?format=json`

  try {
    const response = await fetch(url)

    if (!response.ok) {
      throw new Error('Failed to fetch VIN data')
    }

    const data = await response.json()

    if (!data.Results || data.Results.length === 0) {
      throw new Error('No data returned for this VIN')
    }

    // Parse the results array into a more usable object
    const parsed = parseNHTSAResults(data.Results)

    // Check for errors in the response
    if (parsed.errorCode && parsed.errorCode !== '0') {
      // Error code 0 means no error, other codes indicate issues
      const errorText = parsed.errorText || 'Unable to decode this VIN'
      if (parsed.errorCode === '1') {
        // Partial data available
        console.warn('VIN decode warning:', errorText)
      } else {
        throw new Error(errorText)
      }
    }

    return parsed
  } catch (error) {
    console.error('VIN decode error:', error)
    throw error
  }
}

/**
 * Parse NHTSA API results array into a structured object
 */
function parseNHTSAResults(results) {
  const parsed = {}

  // Map of NHTSA variable names to our field names
  const fieldMap = {
    'Model Year': 'year',
    'Make': 'make',
    'Model': 'model',
    'Trim': 'trim',
    'Body Class': 'bodyClass',
    'Vehicle Type': 'vehicleType',
    'Fuel Type - Primary': 'fuelType',
    'Displacement (L)': 'engineDisplacement',
    'Engine Number of Cylinders': 'engineCylinders',
    'Drive Type': 'driveType',
    'Doors': 'doors',
    'Manufacturer Name': 'manufacturer',
    'Plant City': 'plantCity',
    'Plant Country': 'plantCountry',
    'Series': 'series',
    'Transmission Style': 'transmissionStyle',
    'Engine Model': 'engineModel',
    'Error Code': 'errorCode',
    'Error Text': 'errorText',
    // Additional useful fields
    'GVWR': 'gvwr',
    'Seat Belts': 'seatBelts',
    'Other Engine Info': 'otherEngineInfo',
  }

  results.forEach(item => {
    const fieldName = fieldMap[item.Variable]
    if (fieldName && item.Value && item.Value.trim() !== '') {
      parsed[fieldName] = item.Value.trim()
    }
  })

  return parsed
}

/**
 * Build a descriptive trim/variant string from decoded data
 */
export function buildTrimString(decoded) {
  const parts = []

  if (decoded.trim) {
    parts.push(decoded.trim)
  }

  if (decoded.series && decoded.series !== decoded.trim) {
    parts.push(decoded.series)
  }

  return parts.join(' ').trim() || null
}

/**
 * Build engine description string
 */
export function buildEngineString(decoded) {
  const parts = []

  if (decoded.engineDisplacement) {
    parts.push(`${decoded.engineDisplacement}L`)
  }

  if (decoded.engineCylinders) {
    parts.push(`${decoded.engineCylinders}-cyl`)
  }

  if (decoded.fuelType) {
    parts.push(decoded.fuelType)
  }

  return parts.join(' ').trim() || null
}

/**
 * Get common trim levels for a specific vehicle make
 * Returns curated list of popular trim levels for collector/performance car makes
 * @param {string} make - Vehicle make (e.g., 'Porsche', 'Ferrari')
 * @param {string} model - Vehicle model (currently unused but available for future enhancement)
 * @returns {array} - Array of common trim level strings
 */
export function getCommonTrims(make, model) {
  const trimsByMake = {
    'Porsche': ['Base', 'S', 'GTS', 'GT3', 'GT3 RS', 'GT4', 'Turbo', 'Turbo S', 'Carrera', 'Carrera S', 'Carrera 4S', 'Targa', 'Targa 4S', 'Sport Classic'],
    'BMW': ['Base', 'xDrive', 'M Sport', 'Competition', 'CS', 'CSL', 'M'],
    'Mercedes-Benz': ['Base', 'AMG', 'AMG S', '4MATIC', 'Maybach', 'Black Series', 'Edition 1'],
    'Ferrari': ['Base', 'Spider', 'Speciale', 'Pista', 'Challenge Stradale', 'Scuderia', 'Competizione'],
    'Lamborghini': ['Base', 'Spyder', 'Performante', 'STO', 'SVJ', 'Evo', 'Tecnica'],
    'Ford': ['Base', 'GT', 'GT350', 'GT350R', 'GT500', 'Mach 1', 'Boss 302', 'Shelby', 'Cobra', 'Premium', 'EcoBoost'],
    'Chevrolet': ['Base', 'LT', 'SS', 'ZL1', 'Z06', 'ZR1', 'Grand Sport', 'Stingray', 'Z/28', '1LE', 'RS'],
    'Dodge': ['Base', 'R/T', 'SRT', 'Hellcat', 'Redeye', 'Jailbreak', 'Demon', 'Scat Pack', 'Widebody'],
    'Aston Martin': ['Base', 'V8', 'V12', 'S', 'Volante', 'Zagato', 'AMR', 'GT4'],
    'McLaren': ['Base', 'Spider', 'S', 'LT', 'GT', 'MSO'],
    'Audi': ['Base', 'Premium', 'Premium Plus', 'Prestige', 'S', 'RS', 'Performance', 'Competition'],
    'Jaguar': ['Base', 'S', 'R', 'R-S', 'SVR', 'F-Type', 'Project 8'],
    'Lotus': ['Base', 'Sport', 'Cup', 'GT', 'Exige', 'Final Edition'],
    'Toyota': ['Base', 'Premium', 'GR', 'TRD', 'TRD Pro', 'Limited'],
    'Nissan': ['Base', 'Premium', 'NISMO', 'Track Edition']
  }

  // Default trims for any make not listed
  const defaultTrims = ['Base', 'Sport', 'Premium', 'Limited', 'Performance']

  return trimsByMake[make] || defaultTrims
}

export default {
  decodeVIN,
  buildTrimString,
  buildEngineString,
  getCommonTrims
}
